import { parse, stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@dashboard/components/Button';
import CreateWithAIButton from '@dashboard/components/CreateWithAIButton';
import PageHeader from '@dashboard/components/PageHeader';
import PagesList from '@dashboard/components/PagesList';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

const emptyState = {
  searchTitle: 'No checkout pages found',
  filteredTitle: 'No checkout pages found',
  defaultTitle: 'Create your first checkout page',
  defaultSubtitle: 'Sell products, digital downloads, and subscriptions.',
  createLabel: 'Create checkout page',
};

const CHECKOUT_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'one_time', label: 'One-time' },
  { value: 'recurring', label: 'Recurring' },
];

const DEFAULT_CHECKOUT_FILTER = 'all';

function getFilterFromQuery(value) {
  return CHECKOUT_FILTERS.some(f => f.value === value) ? value : DEFAULT_CHECKOUT_FILTER;
}

// Billing buckets keyed off the accurate `billingModel` (computed from the
// prices). A `mixed` product (both one-time and recurring prices) appears in
// BOTH the One-time and Recurring buckets — there's no separate "mixed" filter,
// since you'd only ever filter for one-time or recurring and a mixed product
// belongs in both. Bucketing off the legacy `productType` is wrong when the two
// disagree — e.g. a wizard product created as `subscription` but priced one-time
// has `billingModel: 'charge'`, so it belongs under One-time, not Recurring.
//
// Fall back to `productType` ONLY for pages with no `billingModel` yet (not yet
// backfilled — see the billingModel migration) so they keep their legacy bucket
// instead of vanishing. Passed as a JSON `$or` so the API's generic parseFilter
// turns it into a Mongo operator (the `$`-prefixed key is preserved as-is).
function getExtraFilter(checkoutFilter) {
  if (checkoutFilter === 'one_time') {
    return {
      $or: JSON.stringify([
        { billingModel: { $in: ['charge', 'mixed'] } },
        { billingModel: { $exists: false }, productType: 'charge' },
      ]),
    };
  }
  if (checkoutFilter === 'recurring') {
    return {
      $or: JSON.stringify([
        { billingModel: { $in: ['subscription', 'mixed'] } },
        { billingModel: { $exists: false }, productType: 'subscription' },
      ]),
    };
  }
  return {};
}

const Checkouts = () => {
  const query = useUrlQuery();
  const history = useHistory();
  const [checkoutFilter, setCheckoutFilter] = useState(getFilterFromQuery(query.type));

  useEffect(() => {
    setCheckoutFilter(getFilterFromQuery(query.type));
  }, [query.type]);

  useEffect(() => {
    const current = parse(history.location.search);
    history.replace({
      search: stringify({ ...current, type: checkoutFilter || DEFAULT_CHECKOUT_FILTER }),
    });
  }, [checkoutFilter, history]);

  return (
    <>
      <div className="p-4 space-y-4">
        <PageHeader
          title="Checkout pages"
          topBarActions={
            <div className="flex items-center gap-2">
              <CreateWithAIButton pageType="checkout" />

              <Button to="/checkouts/new" appearance="primary">
                Create checkout page
              </Button>
            </div>
          }
        />

        <PagesList
          type="checkout"
          emptyState={emptyState}
          createHref="/checkouts/new"
          searchPlaceholder="Search checkout pages"
          extraFilter={getExtraFilter(checkoutFilter)}
          leftControls={
            <ToggleGroup
              type="single"
              variant="outline"
              value={checkoutFilter}
              onValueChange={value => {
                if (value) setCheckoutFilter(value);
              }}
              className="rounded-full"
            >
              {CHECKOUT_FILTERS.map(option => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  className="text-sm first:rounded-l-full last:rounded-r-full"
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          }
        />
      </div>
    </>
  );
};

export default Checkouts;
